import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Nav from './components/Nav';
import Footer from './components/Footer';

const Homepage = lazy(() => import('./pages/Homepage'));
const Discover = lazy(() => import('./pages/Discover'));
const TrendingPage = lazy(() => import('./pages/TrendingPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ToolPage = lazy(() => import('./pages/ToolPage'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const SubmitPage = lazy(() => import('./pages/SubmitPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SkillsPage = lazy(() => import('./pages/SkillsPage'));
const SkillDetailPage = lazy(() => import('./pages/SkillDetailPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const StackPage = lazy(() => import('./pages/StackPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));

function BrowseRedirect() {
  const { pathname } = useLocation();
  const slug = pathname.replace('/browse/', '');
  return <Navigate to={`/category/${slug}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageFallback() {
  return (
    <div style={{
      minHeight: '100svh',
      background: 'var(--dark)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: '28px',
        height: '28px',
        border: '2.5px solid rgba(18,184,160,0.25)',
        borderTopColor: 'var(--teal)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
    </div>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <Nav />
      <main style={{ paddingTop: 'var(--nav-h)' }}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/trends" element={<TrendingPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/browse/:slug" element={<BrowseRedirect />} />
            <Route path="/tool/:slug" element={<ToolPage />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/submit" element={<SubmitPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/skills" element={<SkillsPage />} />
            <Route path="/skills/:slug" element={<SkillDetailPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/my-stack" element={<StackPage />} />
            <Route path="/stack/:shareId" element={<StackPage shared />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: ({ resetError }) => (
    <div style={{
      minHeight: '100svh',
      background: 'var(--dark)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--space-4)',
      color: 'var(--on-dark)',
      fontFamily: 'var(--font-sans)',
      padding: 'var(--space-6)',
      textAlign: 'center',
    }}>
      <p style={{ fontSize: '1.125rem', color: 'var(--teal)' }}>Something went wrong.</p>
      <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
        This has been reported automatically. Try refreshing the page.
      </p>
      <button
        onClick={resetError}
        style={{
          background: 'var(--teal)',
          color: '#fff',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3) var(--space-5)',
          fontFamily: 'var(--font-sans)',
          fontSize: '0.9rem',
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  ),
});
